import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, filter } from 'lodash';
import { Grid } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import 'moment/locale/fr';
import { isCanadianFrench, translateData, getToUrl } from 'utils/translation';
import { Span, Section } from 'components/Ui';
import { ProductListItem } from 'components/ContentfulFields/ProductCard';
import { useIsMobile, useIsVertTablet } from 'utils/hooks';

import PriceSummary from 'containers/PrimoCart/PriceSummary';

import commonStyles from 'containers/PrimoCheckoutPage/styles.scss';
import styles from './styles.scss';
import {
  selectCartData,
  selectCartSummary,
  selectPaymentSurcharges,
} from '../../Cart/selectors';
import { selectOrderData } from '../../CheckoutPage/selectors';

const useCartData = () => {
  const {
    items,
    deliverySetup,
    servicesAddresses: serviceAddresses,
  } = useSelector(selectCartData());
  const summary = useSelector(selectCartSummary());

  return [
    items || [],
    summary || {},
    deliverySetup || {},
    serviceAddresses || [],
  ];
};

const useOrderData = () => {
  const { items, ...summary } = useSelector(selectOrderData());
  const paymentSurcharges = useSelector(selectPaymentSurcharges());

  let summaryWithPaymentSurcharges = { ...summary };

  if (paymentSurcharges?.recurring && summary.recurringCharge) {
    summaryWithPaymentSurcharges = {
      ...summary,
      paymentSurcharges,
      ogTotal: summary.total,
      ogRecurringCharge: summary.recurringCharge,
      total: summary.total + paymentSurcharges.initial.surchargeAmount,
      recurringCharge:
        summary.recurringCharge + paymentSurcharges.recurring.surchargeAmount,
    };
  }

  return [items || [], summaryWithPaymentSurcharges, {}];
};

const CheckoutSummary = ({ source }) => {
  const [items, summary, deliverySetup, serviceAddresses] =
    source === 'order' ? useOrderData() : useCartData();
  const { deliveryDate } = deliverySetup;

  const isMobile = useIsMobile();
  const isVertTablet = useIsVertTablet();
  const md1 = isVertTablet ? 8 : 9;
  const md2 = isVertTablet ? 4 : 3;

  useEffect(() => {
    if (isCanadianFrench()) {
      moment.locale('fr');
    } else {
      moment.locale('en');
    }
  }, []);

  const recurringDeliveryDates =
    get(serviceAddresses, '0.recurringDeliveryDates') || [];
  const futureRecurringDeliveryDates = filter(recurringDeliveryDates, (d) =>
    moment(d.date).isAfter(moment(deliveryDate)),
  );
  const startOfRecurringDelivery = get(futureRecurringDeliveryDates, '0.date');

  // Todo: Implement VTable mode

  return (
    <Section
      className={styles.summaryContainer}
      aria-label="My recurring order"
    >
      {source !== 'order' && (
        <Section
          className={styles.firstDeliveryInfo}
          aria-label="First Delivery Date and Recurring Order Date"
        >
          <div className={styles.block}>
            <Span>First Delivery Date:</Span>
            <span className={styles.subText}>
              {moment(deliveryDate).format('Do MMMM')}
            </span>
          </div>
          <div className={styles.block}>
            <Span>Recurring Order:</Span>
            <span className={styles.subText}>
              {translateData('Every 2 weeks beginning')}{' '}
              {moment(startOfRecurringDelivery).format('MMMM Do')}
            </span>
          </div>
        </Section>
      )}
      <Section aria-label="Recurring Order details">
        <Grid container spacing={isMobile ? 1 : 4}>
          <Grid item md={md1} sm={12}>
            <Grid classes={{ root: styles.productsList }}>
              <div className={styles.orderHeader}>
                <Span className={classnames(commonStyles.title, styles.title)}>
                  My Recurring Order:
                </Span>
                {source === 'cart' && (
                  <Link
                    to={getToUrl('/cart', 'U52')}
                    className={commonStyles.editLink}
                    aria-label="Edit My Recurring Order"
                  >
                    {translateData('Edit')}
                  </Link>
                )}
              </div>
              {items.map((item) => (
                <ProductListItem
                  product={item}
                  className={styles.productItem}
                  key={item.itemNumber}
                  checkoutView
                />
              ))}
            </Grid>
          </Grid>
          <Grid item md={md2} sm={12} className={styles.orderDetails}>
            <PriceSummary {...summary} cartItems={items} />
          </Grid>
        </Grid>
      </Section>
    </Section>
  );
};

CheckoutSummary.defaultProps = {
  source: 'cart',
};

CheckoutSummary.propTypes = {
  source: PropTypes.oneOf(['cart', 'order']),
};

export default CheckoutSummary;
